import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { EconomicCalendarEvent } from "../../hooks/useEconomicCalendar";
import { trimEconomicEventName } from "./economicEventName";
import { PANEL_ROW_HEIGHT } from "./economicCalendarSelection";
import {
  displayCountry,
  eventTime,
  formatEconomicValue,
  impactColorOf,
  isFinitely,
} from "./economicEventFormat";
import { panelBorder, panelSurface } from "./overviewPanel";

// One economic event on the house card surface — shared by the dashboard's
// calendar panel and the full calendar so the two always read identically.
// (The panel's event ordering lives in economicCalendarSelection.)
//
// The card is a fixed height whether or not it has a figures line: a speech
// and a print are the same size, so a rail of them holds its rhythm and a
// day of them scans as a list rather than a stack of unequal boxes.

// The time column's width, and therefore where the figures line hangs from.
const TIME_COLUMN_WIDTH = 44;

const EconomicEventRow: React.FC<{
  event: EconomicCalendarEvent;
  /** Today's rows pulse while their release is pending; preview rows from
   *  later days sit still — a wall of pulsing dots reads as noise. */
  pulse: boolean;
  /** A pulled-forward row says which day it belongs to, above its time, so
   *  the rail needs no date labels between its cards. */
  dateLabel?: string;
}> = ({ event, pulse, dateLabel }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const impactColor = impactColorOf(event);
  const country = displayCountry(event);
  const actualReleased = isFinitely(event.actual);

  // A speech or meeting carries no numbers at all — the figures line only
  // earns its row when SOMETHING is measurable. Within it, only the stats
  // that exist render (CFTC positions have a Prev but never an Est); Act
  // always shows once any figure exists, because pending-vs-landed is the
  // line's whole story.
  const hasFigures =
    isFinitely(event.previous) || isFinitely(event.estimate) || actualReleased;
  const stats = [
    ...(isFinitely(event.previous)
      ? [
          {
            label: "Prev",
            value: formatEconomicValue(event.previous, event.unit),
          },
        ]
      : []),
    ...(isFinitely(event.estimate)
      ? [
          {
            label: "Est",
            value: formatEconomicValue(event.estimate, event.unit),
          },
        ]
      : []),
  ];
  const actual = formatEconomicValue(event.actual, event.unit);

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        height: PANEL_ROW_HEIGHT,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 0.25,
        px: 1.5,
        minWidth: 0,
        borderRadius: 1.5,
        backgroundColor: panelSurface(isDark),
        border: `1px solid ${panelBorder(isDark)}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: TIME_COLUMN_WIDTH,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {dateLabel && (
            <Typography
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: theme.palette.text.secondary,
                whiteSpace: "nowrap",
              }}
            >
              {dateLabel}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: "0.625rem",
              lineHeight: 1.3,
              color: theme.palette.text.secondary,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {eventTime(event)}
          </Typography>
        </Box>
        {/* Pulses while the release is pending; settles solid once it lands */}
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: impactColor,
            flexShrink: 0,
            ...(pulse &&
              !actualReleased && {
                animation: "econDotPulse 2.2s ease-in-out infinite",
                "@keyframes econDotPulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                  "100%": { opacity: 1 },
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }),
          }}
        />
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            color: theme.palette.text.secondary,
            flexShrink: 0,
          }}
        >
          {country}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 500,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {trimEconomicEventName(event.event)}
        </Typography>
      </Box>

      {/* Figures on their own line, hanging past the time column. Prev and
          Est read as one muted group; Act stands apart on the right — the
          only number here that is news. */}
      {hasFigures && (
        <Box
          sx={{
            pl: `${TIME_COLUMN_WIDTH + 8}px`,
            display: "flex",
            alignItems: "baseline",
            gap: 2,
            minWidth: 0,
          }}
        >
          {stats.map((stat) => (
            <Typography
              key={stat.label}
              sx={{
                fontSize: "0.625rem",
                color: theme.palette.text.secondary,
                whiteSpace: "nowrap",
              }}
            >
              {stat.label}:{" "}
              <Box
                component="span"
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  fontVariantNumeric: "tabular-nums",
                  ml: 0.25,
                }}
              >
                {stat.value}
              </Box>
            </Typography>
          ))}
          <Box sx={{ flex: 1 }} />
          <Typography
            sx={{
              fontSize: "0.625rem",
              color: theme.palette.text.secondary,
              whiteSpace: "nowrap",
            }}
          >
            Act:{" "}
            <Box
              component="span"
              sx={{
                // Same size as Prev/Est so the figures share one clean
                // baseline; weight and color alone mark it as the news.
                fontSize: "0.6875rem",
                fontWeight: 700,
                color: actualReleased
                  ? theme.palette.text.primary
                  : theme.palette.text.disabled,
                fontVariantNumeric: "tabular-nums",
                ml: 0.25,
              }}
            >
              {actual}
            </Box>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EconomicEventRow;
